import { useEffect, useState } from "react";

export type ThemeMode = "light" | "dark";

const THEME_KEY = "theme-preference";
const THEME_EVENT = "guardai-themechange";

export function loadStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  window.localStorage.setItem(THEME_KEY, mode);
  // Notify any other toggle in the app (header switch ↔ profile menu) so they
  // all reflect the same state.
  window.dispatchEvent(new CustomEvent<ThemeMode>(THEME_EVENT, { detail: mode }));
}

// Shared hook so every theme control stays in sync regardless of where it
// lives in the tree. Reflects (and persists) a single global theme.
export function useTheme(): [ThemeMode, () => void, (mode: ThemeMode) => void] {
  const [mode, setMode] = useState<ThemeMode>(() => loadStoredTheme());

  useEffect(() => {
    // Make sure the DOM matches the stored preference on mount.
    applyTheme(mode);
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<ThemeMode>).detail;
      if (next === "dark" || next === "light") setMode(next);
    };
    window.addEventListener(THEME_EVENT, onChange);
    return () => window.removeEventListener(THEME_EVENT, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (next: ThemeMode) => {
    setMode(next);
    applyTheme(next);
  };
  const toggle = () => set(mode === "dark" ? "light" : "dark");

  return [mode, toggle, set];
}
